import {
  type GameState,
  type GameStateDelta,
  postDataTagMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@win-reality/react-ui";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setCurrentBattingPracticeHitter,
  updateBattingPracticeLeaders,
} from "../../../store/slices/battingPractice";
import { resetEventQueue } from "../../../store/slices/socket";
import { selectRecentEventForReview } from "../../../store/slices/socket/selectors";
import { setModalState } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import type { IPlayer } from "../../../store/types";
import { sideTypeMap } from "../../../utils/api-helpers";
import { sortAlphabetic } from "../../../utils/sorters";
import { useUpdateGameState } from "../../../utils/useUpdateGameState";
import { TilesContainer } from "../../shared/TilesContainer";
import { LandingVis } from "../../shared/VisualizationContainer/visualizations/LandingVis";
import { LaunchAngle } from "../../shared/VisualizationContainer/visualizations/LaunchAngle";
import { Leaderboard } from "./Leaderboard";
import { PlayerRow } from "./PlayerRow";

export const BattingPracticeView = () => {
  const dispatch = useDispatch();
  const { id: machineId } = useParams() as { id: string };
  const updateGameState = useUpdateGameState();
  const lastAutoSavedUuid = useRef<string | null>(null);

  const recentEvent = useSelector(selectRecentEventForReview);
  const gameUuid = useSelector((state: RootState) => state.session.uuid);
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const { roster, teamName, currentHitter, exitVeloLeaders, distanceLeaders } =
    useSelector((state: RootState) => state.battingPractice);

  const sortedRoster = sortAlphabetic(roster, "lastName");

  const hasCurrentHitter = !!currentHitter?.uuid;

  // Passive auto-save mutation
  const { mutate: savePassiveTag } = useMutation({
    ...postDataTagMutation(),
    onError: (error: Error) => {
      console.error("BP passive auto-save failed:", error);
    },
  });

  // Auto-save: When a new event arrives and a hitter is selected, save passively
  useEffect(() => {
    const eventUuid = recentEvent?.event_uuid;
    if (!eventUuid || !gameUuid || !machineId || !hasCurrentHitter) return;
    if (eventUuid === lastAutoSavedUuid.current) return;
    if (recentEvent.tagData) return;

    lastAutoSavedUuid.current = eventUuid;

    const hitterEntry = {
      externalId: currentHitter.uuid,
      hitSide: sideTypeMap[currentHitter.batHand] ?? "right",
      pitchSide: sideTypeMap[currentHitter.throwHand] ?? "right",
      playerName:
        `${currentHitter.lastName}, ${currentHitter.firstName}`.trim(),
      position: "pitcher" as const,
    };

    savePassiveTag(
      {
        body: {
          payload: {
            event_uuid: eventUuid,
            event_time: recentEvent.event_time,
            machine_uuid: machineId,
            tag_data: {
              metadata: {} as GameStateDelta,
              gameState: {
                ...(gameState as GameState),
                currentHitter: hitterEntry,
              },
            },
          },
        },
      },
      {
        onSuccess: (data) => {
          if (data.gameState && data.game) {
            updateGameState(data.gameState, data.game);
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentEvent?.event_uuid, hasCurrentHitter]);

  // Leaderboard update: When a new event with hit_data arrives, update leaderboards
  useEffect(() => {
    if (!recentEvent?.event_uuid || !hasCurrentHitter) return;
    if (recentEvent.hit_data) {
      dispatch(
        updateBattingPracticeLeaders({
          event: recentEvent,
          player: currentHitter,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentEvent?.event_uuid, hasCurrentHitter]);

  const handleSelectHitter = (player: IPlayer) => {
    dispatch(resetEventQueue());
    dispatch(setCurrentBattingPracticeHitter({ player }));
  };

  return (
    <div className="flex h-[calc(100vh-var(--navbar-height))] gap-4 px-6 py-4 sm:px-4">
      {/* Left Column: Roster Panel */}
      <div className="flex w-[25%] min-w-[200px] flex-col overflow-hidden rounded bg-white shadow-md">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold uppercase text-charcoal-900">
            {teamName ? `Roster: ${teamName}` : "No Roster Added"}
          </h3>
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-2">
          {sortedRoster.map((player) => (
            <PlayerRow
              key={player.uuid}
              player={player}
              isActive={currentHitter?.uuid === player.uuid}
              onClick={() => handleSelectHitter(player)}
            />
          ))}
        </div>
      </div>

      {/* Right Column: Data + Visualizations + Leaderboards */}
      <div className="flex flex-1 flex-col gap-4">
        {/* Data Tiles */}
        <TilesContainer tileType="hitting" direction="row" maxTiles={3} />

        {/* Visualizations */}
        <div className="flex gap-4">
          <div
            className="flex flex-1 flex-col items-center justify-center rounded bg-white shadow-md"
            style={{ height: "19.3rem" }}
          >
            <LandingVis style={{ width: "100%", marginBottom: "10px" }} />
          </div>
          <div
            className="flex flex-1 flex-col items-center justify-center rounded bg-white shadow-md"
            style={{ height: "19.3rem" }}
          >
            <LaunchAngle style={{ width: "100%" }} />
          </div>
        </div>

        {/* Leaderboards */}
        <div className="flex gap-4">
          <div className="flex-1">
            <Leaderboard
              label="Exit Velo"
              records={exitVeloLeaders}
              units="MPH"
            />
          </div>
          <div className="flex-1">
            <Leaderboard
              label="Projected Distance"
              records={distanceLeaders}
              units="FT"
            />
          </div>
        </div>

        {/* End Session */}
        <div className="flex justify-start">
          <Button
            variant="secondary-flat-light"
            onClick={() =>
              dispatch(setModalState({ state: "End Session", data: {} }))
            }
          >
            End Batting Practice
          </Button>
        </div>
      </div>
    </div>
  );
};
